'use client'

import { RefObject, useCallback, useEffect, useState } from 'react'

export interface UsePictureInPictureReturn {
  enterPiP: () => Promise<void>
  exitPiP: () => Promise<void>
  isPiPActive: boolean
  isPiPSupported: boolean
}

export function usePictureInPicture(videoRef: RefObject<HTMLVideoElement | null>): UsePictureInPictureReturn {
  const [isPiPActive, setIsPiPActive] = useState(false)
  const [isPiPSupported, setIsPiPSupported] = useState(false)

  // Check PiP support on mount
  useEffect(() => {
    setIsPiPSupported(
      typeof document !== 'undefined' && 'pictureInPictureEnabled' in document && document.pictureInPictureEnabled,
    )
  }, [])

  // Listen for PiP events
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleEnterPiP = () => setIsPiPActive(true)
    const handleLeavePiP = () => setIsPiPActive(false)

    video.addEventListener('enterpictureinpicture', handleEnterPiP)
    video.addEventListener('leavepictureinpicture', handleLeavePiP)

    return () => {
      video.removeEventListener('enterpictureinpicture', handleEnterPiP)
      video.removeEventListener('leavepictureinpicture', handleLeavePiP)
    }
  }, [videoRef])

  const enterPiP = useCallback(async () => {
    const video = videoRef.current
    if (!video || !isPiPSupported) return

    try {
      // Ensure video is playing before entering PiP
      if (video.paused) {
        await video.play()
      }
      await video.requestPictureInPicture()
    } catch (error) {
      console.error('Failed to enter Picture-in-Picture:', error)
    }
  }, [videoRef, isPiPSupported])

  const exitPiP = useCallback(async () => {
    if (!isPiPSupported || !document.pictureInPictureElement) return

    try {
      await document.exitPictureInPicture()
    } catch (error) {
      console.error('Failed to exit Picture-in-Picture:', error)
    }
  }, [isPiPSupported])

  return {
    enterPiP,
    exitPiP,
    isPiPActive,
    isPiPSupported,
  }
}

export default usePictureInPicture
